import { useQuery } from '@tanstack/react-query';

import { logisticsApi } from 'api/logistics';
import { parseCrossDockPurchaseOrderId } from 'components/logistics/components/crossDockPurchaseOrders/utils/crossDockPurchaseOrderUtils';

export const crossDockPurchaseOrderQueryKeys = {
    all: ['crossDockPurchaseOrders'],
    search: (params) => [...crossDockPurchaseOrderQueryKeys.all, 'search', params],
    detail: (id) => [...crossDockPurchaseOrderQueryKeys.all, 'detail', id],
};

/**
 * @param {{ limit: number, offset: number }} params
 * @param {import('@tanstack/react-query').UseQueryOptions} [options]
 */
export const useCrossDockPurchaseOrdersSearch = (params, options = {}) =>
    useQuery({
        queryKey: crossDockPurchaseOrderQueryKeys.search(params),
        queryFn: async ({ signal }) => {
            const response = await logisticsApi.listCrossDockPurchaseOrders(params, { signal });
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch cross dock purchase orders');
            }
            return {
                rows: response.data?.rows ?? [],
                pagination: response.data?.pagination ?? {},
            };
        },
        staleTime: 30 * 1000,
        ...options,
    });

/**
 * @param {{ id?: string|number, enabled?: boolean }} params
 */
export const useCrossDockPurchaseOrderQuery = ({ enabled = true, id } = {}) => {
    const parsedId = parseCrossDockPurchaseOrderId(id);

    return useQuery({
        queryKey: crossDockPurchaseOrderQueryKeys.detail(parsedId),
        queryFn: async ({ signal }) => {
            const response = await logisticsApi.getCrossDockPurchaseOrder(parsedId, { signal });
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch cross dock purchase order');
            }
            const detail = response.data?.crossDockPurchaseOrder;
            if (!detail) {
                throw new Error('Cross dock purchase order not found');
            }
            return detail;
        },
        enabled: parsedId != null && enabled,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        staleTime: 30 * 1000,
    });
};
